// Правая колонка «Сегодня»: неделя и вся карта. Макет —
// design/redesign.mockup.html, решение — docs/02-decisions.md, 2026-10-28.
//
// Отвечают на вопрос владельца «как я работал на прошлой неделе и насколько
// приблизился к цели»: до этого главная показывала окно «дни из 21» и число
// пройденного, но не сравнение недель и не движение по ним.
//
// Числа вместо оценок, как везде: «было 14», а не «лучше, чем на прошлой».
// Прогноза балла нет — решение 15; темп считается в навыках.

import type { ProgressSnapshot, WeekSummary } from "../api";
import { plural } from "../lib/dates";
import { CoachNote } from "./CoachNote";
import { IconChevron } from "./Icons";

const MONTHS = ["янв", "фев", "мар", "апр", "мая", "июн", "июл", "авг", "сен", "окт", "ноя", "дек"];

/** «26 окт – 1 ноя» или «19–25 окт». */
function range(from: string, to: string): string {
  const [, fm, fd] = from.split("-").map(Number);
  const [, tm, td] = to.split("-").map(Number);
  return fm === tm ? `${fd}–${td} ${MONTHS[tm! - 1]}` : `${fd} ${MONTHS[fm! - 1]} – ${td} ${MONTHS[tm! - 1]}`;
}

/** «12.10» под столбиком. */
function dayMonth(date: string): string {
  const [, m, d] = date.split("-");
  return `${Number(d)}.${m}`;
}

/** Прирост со знаком: «+2», «−1», «0»; неизвестный — тире. */
function signed(n: number | null): string {
  if (n === null) return "—";
  if (n > 0) return `+${n}`;
  if (n < 0) return `−${-n}`;
  return "0";
}

function share(w: WeekSummary): string {
  return w.tasks === 0 ? "—" : `${Math.round((w.correct / w.tasks) * 100)}%`;
}

const one = (n: number) => n.toLocaleString("ru-RU", { maximumFractionDigits: 1 });

export function WeekCard({
  weeks,
  daysPerWeek,
  learnerKey,
}: {
  weeks: ProgressSnapshot["weeks"];
  /** Обязательство ученика — мерка, по которой тренер судит прошлую неделю. */
  daysPerWeek: number;
  learnerKey: string;
}) {
  const { current: c, previous: p } = weeks;
  const stats: { k: string; now: string; was: string }[] = [
    { k: plural(c.days, "день", "дня", "дней"), now: String(c.days), was: String(p.days) },
    { k: plural(c.tasks, "задача", "задачи", "задач"), now: String(c.tasks), was: String(p.tasks) },
    { k: "верно", now: share(c), was: share(p) },
    { k: "навыков", now: signed(c.passed), was: signed(p.passed) },
  ];
  const max = Math.max(1, ...weeks.chart.map((w) => w.passed ?? 0));
  const last = weeks.chart.length - 1;
  return (
    <section className="card wk-card" aria-labelledby="wk-h">
      <div className="wk-head">
        <h3 id="wk-h">Неделя</h3>
        <span className="eyebrow">{range(c.from, c.to)}</span>
      </div>
      <div className="wk-stats">
        {stats.map((s) => (
          <div key={s.k}>
            <b className="num">{s.now}</b>
            <span>{s.k}</span>
            <small className="num">было {s.was}</small>
          </div>
        ))}
      </div>
      <div
        className="wk-bars"
        role="img"
        aria-label={`Пройдено навыков по неделям, последние ${weeks.chart.length}: ${weeks.chart
          .map((w) => `${dayMonth(w.from)} — ${signed(w.passed)}`)
          .join(", ")}; последняя неделя ещё идёт`}
        title="Столбик — сколько навыков стало пройдено за неделю. Пунктир — неделя ещё идёт."
      >
        {weeks.chart.map((w, i) => (
          <div key={w.from}>
            <i
              className={w.passed === null ? "none" : i === last ? "now" : undefined}
              style={{ height: `${Math.max(3, (Math.max(0, w.passed ?? 0) / max) * 48)}px` }}
            >
              <b className="num">{signed(w.passed)}</b>
            </i>
            <small>{dayMonth(w.from)}</small>
          </div>
        ))}
      </div>
      {/*
        Тренер об итоге ПРОШЛОЙ недели — она закончилась, эта ещё идёт, и судить
        её в понедельник было бы нечестно. Мерка — обязательство ученика. Нет
        прошлой недели (ученик пришёл позже) — тренер молчит.
      */}
      {p.passed === null ? null : (
        <CoachNote
          className="wk-coach"
          situation={p.days >= daysPerWeek ? "weekUp" : "weekDown"}
          vars={{ days: p.days }}
          seedParts={[learnerKey, p.from]}
        />
      )}
    </section>
  );
}

export function MapCard({
  snapshot,
  visit,
  pace,
  onOpenProgress,
}: {
  snapshot: ProgressSnapshot;
  /** «Был вчера» и т. п. — src/lib/greeting.ts. */
  visit: string | null;
  /** «При нынешнем темпе к экзамену…» — src/lib/greeting.ts. */
  pace: string | null;
  onOpenProgress: () => void;
}) {
  const { skills, tempo, window: win } = snapshot;
  const fill = skills.total > 0 ? (skills.passed / skills.total) * 100 : 0;
  return (
    <section className="card map-card" aria-labelledby="map-h">
      <div className="wk-head">
        <h3 id="map-h">Вся карта</h3>
        <button type="button" className="btn btn--icon" onClick={onOpenProgress}>
          Прогресс <IconChevron size={14} />
        </button>
      </div>
      <div className="map-big num">
        {skills.passed}
        <small> / {skills.total}</small>
        {snapshot.progress.today === null ? null : (
          <span className="map-today">сегодня {signed(snapshot.progress.today)}</span>
        )}
      </div>
      <div className="map-track" aria-hidden="true">
        <i style={{ width: `${Math.min(100, fill)}%` }} />
      </div>
      <dl className="map-pace">
        {/* Темп — в ОТКРЫТЫХ навыках, как у полосы «Прогресса» (src/ui/Meter.tsx):
            сервер считает открытия за 14 дней и непокрытое банком не требует.
            Прирост в «Неделе» — пройденные; подписи разные намеренно. */}
        <div>
          <dt>Открываешь в неделю</dt>
          <dd className="num">≈ {one(tempo.actualPerWeek)}</dd>
        </div>
        <div>
          <dt>Чтобы открыть всё</dt>
          <dd className="num">{Math.ceil(tempo.needPerWeek)} в неделю</dd>
        </div>
        <div>
          <dt>Дней за три недели</dt>
          <dd className="num">
            {win.days} из {win.target}
          </dd>
        </div>
      </dl>
      {pace === null && visit === null ? null : (
        <p className="map-note">{[visit, pace].filter(Boolean).join(" · ")}</p>
      )}
    </section>
  );
}
